import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Chip, CircularProgress, Autocomplete,
  TextField, Paper, Avatar, LinearProgress,
} from '@mui/material';
import { loadBase, loadPlayerStats } from '../../services/espnService';
import { useConfig, CONFIG_DEFAULTS } from '../../hooks/useConfig';

const POSITIONS = ['QB', 'RB', 'WR', 'TE'];

const MATCHUP_TIERS = {
  ARI: 'Favorable', ATL: 'Average', BAL: 'Tough', BUF: 'Tough',
  CAR: 'Favorable', CHI: 'Average', CIN: 'Average', CLE: 'Average',
  DAL: 'Average', DEN: 'Average', DET: 'Favorable', GB: 'Average',
  HOU: 'Average', IND: 'Average', JAX: 'Favorable', KC: 'Tough',
  LAC: 'Average', LAR: 'Average', LV: 'Favorable', MIA: 'Average',
  MIN: 'Average', NE: 'Favorable', NO: 'Average', NYG: 'Favorable',
  NYJ: 'Average', PHI: 'Tough', PIT: 'Tough', SEA: 'Average',
  SF: 'Tough', TB: 'Average', TEN: 'Favorable', WAS: 'Average',
};
const MATCHUP_COLOR = { Favorable: '#2ea84b', Average: '#f5a623', Tough: '#ef4444' };

function getStat(catMap, catName, playerId) {
  return catMap?.[catName]?.[playerId]?.num ?? 0;
}

function calcFantasyScore(pos, playerId, catMap, formula, playerStats) {
  // Use playerStats (per-player ESPN endpoint) as the single source of truth.
  // This avoids season/data mismatches between the leaders catMap and per-player stats.
  const s = playerStats?.[playerId];
  const gp = s?.gamesPlayed || 1;
  let total = 0;
  if (pos === 'QB') {
    const f = formula || CONFIG_DEFAULTS.nr_formula_qb;
    total = (s?.passingTouchdowns||0) * (f.passTd ?? 5)
          + (s?.passingYards||0)      * (f.passYards ?? 0.04)
          + (s?.interceptions||0)     * (f.int ?? -2)
          + (s?.rushingYards||0)      * (f.rushYards ?? 0.1)
          + (s?.rushingTouchdowns||0) * (f.rushTd ?? 6);
  } else if (pos === 'RB') {
    const f = formula || CONFIG_DEFAULTS.nr_formula_rb;
    total = (s?.rushingYards||0)      * (f.rushYards ?? 0.1)
          + (s?.rushingTouchdowns||0) * (f.rushTd ?? 6)
          + (s?.receptions||0)        * (f.receptions ?? 1)
          + (s?.receivingYards||0)    * (f.recYards ?? 0.1);
  } else {
    // WR / TE
    const f = formula || CONFIG_DEFAULTS[`nr_formula_${pos.toLowerCase()}`] || CONFIG_DEFAULTS.nr_formula_wr;
    total = (s?.receptions||0)          * (f.receptions ?? 1)
          + (s?.receivingYards||0)      * (f.recYards ?? 0.1)
          + (s?.receivingTouchdowns||0) * (f.recTd ?? 6);
  }
  return total / gp;
}


const POS_CAPS = { QB: 32, RB: 32, WR: 96, TE: 32 };

function calcFptsPG(playerId, pos, playerStats) {
  const s = playerStats[playerId];
  if (!s || !s.gamesPlayed) return 0;
  const gp = s.gamesPlayed;
  let raw = 0;
  if (pos === 'QB') {
    raw = (s.passingYards||0)*0.04 + (s.passingTouchdowns||0)*5 + (s.interceptions||0)*(-2) + (s.rushingYards||0)*0.1 + (s.rushingTouchdowns||0)*6;
  } else if (pos === 'RB') {
    raw = (s.rushingYards||0)*0.1 + (s.rushingTouchdowns||0)*6 + (s.receptions||0)*1 + (s.receivingYards||0)*0.1;
  } else {
    raw = (s.receptions||0)*1 + (s.receivingYards||0)*0.1 + (s.receivingTouchdowns||0)*6;
  }
  return raw / gp;
}

function meetsMinThreshold(playerId, pos, playerStats) {
  const s = playerStats[playerId];
  if (!s) return false;
  if (pos === 'QB') return (s.passingAttempts||0) >= 200;
  if (pos === 'RB') return (s.rushingAttempts||0) >= 50 || (s.receptions||0) >= 20;
  if (pos === 'WR') return (s.receptions||0) >= 20;
  if (pos === 'TE') return (s.receptions||0) >= 10;
  return false;
}

// Ranks players by FPTS/game (PPR). Tied players share rank (skip-rank).
function computeNrRanks(pos, players, playerStats) {
  const eligible = Object.values(players)
    .filter(p => p.posKey === pos && meetsMinThreshold(p.id, pos, playerStats))
    .map(p => ({ id: p.id, fptsPG: calcFptsPG(p.id, pos, playerStats) }))
    .sort((a, b) => b.fptsPG - a.fptsPG);

  const ranks = {};
  const cap = POS_CAPS[pos] ?? 33;

  let rank = 1, i = 0;
  while (i < eligible.length) {
    const tieScore = eligible[i].fptsPG;
    let j = i;
    while (j < eligible.length && eligible[j].fptsPG === tieScore) {
      ranks[eligible[j].id] = rank;
      j++;
    }
    rank += j - i;
    i = j;
  }

  // Non-qualifiers get cap rank
  Object.values(players).filter(p => p.posKey === pos && !ranks[p.id]).forEach(p => {
    ranks[p.id] = cap;
  });

  return { ranks, totalRanked: eligible.length };
}

function getStatLine(pos, playerId, catMap) {
  if (pos === 'QB') {
    return {
      'Pass Yds': getStat(catMap, 'passingYards',       playerId),
      'Pass TD':  getStat(catMap, 'passingTouchdowns',  playerId),
      'INT':      getStat(catMap, 'interceptions',      playerId),
      'Rush Yds': getStat(catMap, 'rushingYards',       playerId),
    };
  }
  if (pos === 'RB') {
    return {
      'Rush Yds': getStat(catMap, 'rushingYards',      playerId),
      'Rush TD':  getStat(catMap, 'rushingTouchdowns', playerId),
      'Rec':      getStat(catMap, 'receptions',        playerId),
      'Rec Yds':  getStat(catMap, 'receivingYards',    playerId),
    };
  }
  return {
    'Receptions': getStat(catMap, 'receptions',          playerId),
    'Rec Yds':    getStat(catMap, 'receivingYards',       playerId),
    'Rec TD':     getStat(catMap, 'receivingTouchdowns',  playerId),
  };
}

function generateReasoning(p1, p2, rank1, rank2, totalRanked, matchup1, matchup2, pos) {
  const r1 = rank1 ?? Infinity;
  const r2 = rank2 ?? Infinity;
  const isP1Start  = r1 <= r2;
  const winner     = isP1Start ? p1 : p2;
  const loser      = isP1Start ? p2 : p1;
  const winRank    = isP1Start ? rank1 : rank2;
  const loseRank   = isP1Start ? rank2 : rank1;
  const winMatchup = isP1Start ? matchup1 : matchup2;
  const loseMatchup= isP1Start ? matchup2 : matchup1;

  const rankNote = winRank != null
    ? `${winner.name} ranks #${winRank} among ${pos}s (${loser.name} is ${loseRank != null ? `#${loseRank}` : 'unranked'})`
    : `${winner.name} has more qualifying fantasy production than ${loser.name}`;

  const matchupNote = winMatchup !== loseMatchup
    ? ` ${winner.name} also draws a ${winMatchup.toLowerCase()} matchup vs. a ${loseMatchup.toLowerCase()} look for ${loser.name}.`
    : '';

  return `Start ${winner.name} — ${rankNote} by Fantasy Rank out of ${totalRanked} qualifying starters.${matchupNote} Stats based on full-season totals. Always check injury reports before lineup lock.`;
}

// ── Player card ──────────────────────────────────────────────────────────────
function PlayerCard({ player, catMap, pos, isStart, nrRank, totalRanked, pts, matchup }) {
  const statLine = getStatLine(pos, player.id, catMap);
  const barScore = nrRank != null
    ? Math.max(0, Math.round((1 - (nrRank - 1) / Math.max(totalRanked - 1, 1)) * 100))
    : 0;

  return (
    <Box sx={{
      flex: 1, minWidth: 0,
      backgroundColor: 'rgba(16,32,52,0.62)',
      border: `1px solid ${isStart ? '#2ea84b' : '#1e3448'}`,
      borderRadius: 2, p: 2,
      boxShadow: isStart ? '0 0 20px rgba(46,168,75,0.22)' : 'none',
      transition: 'all 0.3s',
    }}>
      {/* Badge row */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Chip
          label={isStart ? 'START' : 'SIT'}
          size="small"
          sx={{
            fontWeight: 800, fontSize: '0.72rem', letterSpacing: '0.1em',
            backgroundColor: isStart ? 'rgba(46,168,75,0.18)' : 'rgba(239,68,68,0.15)',
            color: isStart ? '#2ea84b' : '#ef4444',
            border: `1px solid ${isStart ? '#2ea84b' : '#ef4444'}`,
            boxShadow: isStart ? '0 0 8px rgba(46,168,75,0.45)' : 'none',
          }}
        />
        <Typography variant="caption" sx={{ color: MATCHUP_COLOR[matchup] ?? '#9eb8d0', fontWeight: 700, fontSize: '0.68rem' }}>
          {matchup ?? 'Unknown'} Matchup
        </Typography>
      </Box>

      {/* Avatar + name */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
        {player.headshot ? (
          <Avatar src={player.headshot} sx={{ width: 46, height: 46 }} />
        ) : (
          <Avatar sx={{ width: 46, height: 46, bgcolor: 'rgba(245,166,35,0.28)', fontSize: '1.1rem' }}>
            {player.name[0]}
          </Avatar>
        )}
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {player.name}
          </Typography>
          <Typography variant="caption" sx={{ color: '#9eb8d0' }}>{player.team} · {pos}</Typography>
        </Box>
      </Box>

      {/* Fantasy Rank badge */}
      <Box sx={{
        mb: 1.75, p: '8px 12px', borderRadius: 1.5,
        backgroundColor: isStart ? 'rgba(46,168,75,0.1)' : 'rgba(5,12,22,0.5)',
        border: `1px solid ${isStart ? 'rgba(46,168,75,0.25)' : 'rgba(30,52,72,0.8)'}`,
      }}>
        <Typography variant="caption" sx={{ color: '#9eb8d0', fontSize: '0.6rem', display: 'block', mb: 0.25 }}>
          Fantasy Rank
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
          <Typography sx={{ fontWeight: 900, fontSize: '1.4rem', lineHeight: 1, color: isStart ? '#2ea84b' : '#e8f0f8' }}>
            {nrRank != null ? `#${nrRank}` : '—'}
          </Typography>
          {totalRanked > 0 && (
            <Typography sx={{ fontSize: '0.65rem', color: '#4a6080' }}>of {totalRanked} {pos}s</Typography>
          )}
        </Box>
        <LinearProgress
          variant="determinate"
          value={barScore}
          sx={{
            mt: 0.75, height: 4, borderRadius: 3,
            backgroundColor: 'rgba(30,52,72,0.8)',
            '& .MuiLinearProgress-bar': { backgroundColor: isStart ? '#2ea84b' : '#ef4444', borderRadius: 3 },
          }}
        />
      </Box>

      {/* Stat grid */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75 }}>
        {Object.entries(statLine).map(([label, val]) => (
          <Box key={label} sx={{ backgroundColor: 'rgba(5,12,22,0.5)', borderRadius: 1, p: '6px 8px' }}>
            <Typography variant="caption" sx={{ color: '#9eb8d0', fontSize: '0.6rem', display: 'block', mb: 0.15 }}>{label}</Typography>
            <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#e8f0f8' }}>{val}</Typography>
          </Box>
        ))}
        <Box sx={{ backgroundColor: 'rgba(5,12,22,0.5)', borderRadius: 1, p: '6px 8px' }}>
          <Typography variant="caption" sx={{ color: '#9eb8d0', fontSize: '0.6rem', display: 'block', mb: 0.15 }}>Est. Pts/G</Typography>
          <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#f5a623' }}>{pts.toFixed(1)}</Typography>
        </Box>
      </Box>
    </Box>
  );
}

// ── Main component ───────────────────────────────────────────────────────────
export default function StartSitTool() {
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [players, setPlayers]       = useState({});
  const [catMap, setCatMap]         = useState({});
  const [playerStats, setPlayerStats] = useState({});
  const [position, setPosition]     = useState('WR');
  const [player1, setPlayer1]       = useState(null);
  const [player2, setPlayer2]       = useState(null);

  const { config } = useConfig();

  useEffect(() => {
    setLoading(true);
    loadBase()
      .then(({ players: p, catMap: c }) => {
        setPlayers(p);
        setCatMap(c);
        return loadPlayerStats(Object.keys(p));
      })
      .then(stats => { setPlayerStats(stats); setLoading(false); })
      .catch(() => { setError('Failed to load player data.'); setLoading(false); });
  }, []);

  const posPlayers = useMemo(() => (
    Object.values(players).filter(p => p.posKey === position).sort((a, b) => a.name.localeCompare(b.name))
  ), [players, position]);

  const handlePositionChange = (pos) => { setPosition(pos); setPlayer1(null); setPlayer2(null); };

  // NR ranks for all players at current position (FPTS/game)
  const { ranks, totalRanked } = useMemo(() => {
    if (!Object.keys(players).length || !Object.keys(playerStats).length) return { ranks: {}, totalRanked: 0 };
    return computeNrRanks(position, players, playerStats);
  }, [players, playerStats, position]);

  const comparison = useMemo(() => {
    if (!player1 || !player2) return null;
    const formula  = config[`nr_formula_${position.toLowerCase()}`] || CONFIG_DEFAULTS[`nr_formula_${position.toLowerCase()}`];
    const pts1     = calcFantasyScore(position, player1.id, catMap, formula, playerStats);
    const pts2     = calcFantasyScore(position, player2.id, catMap, formula, playerStats);
    const rank1    = ranks[player1.id] ?? null;
    const rank2    = ranks[player2.id] ?? null;
    const matchup1 = MATCHUP_TIERS[player1.team] ?? 'Average';
    const matchup2 = MATCHUP_TIERS[player2.team] ?? 'Average';
    const p1Starts = (rank1 ?? Infinity) <= (rank2 ?? Infinity);
    const reasoning = generateReasoning(player1, player2, rank1, rank2, totalRanked, matchup1, matchup2, position);
    return { pts1, pts2, rank1, rank2, matchup1, matchup2, p1Starts, reasoning };
  }, [player1, player2, position, catMap, playerStats, ranks, totalRanked, config]);

  if (loading) return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 2 }}>
      <CircularProgress size={36} sx={{ color: '#2ea84b' }} />
      <Typography variant="body2" sx={{ color: '#9eb8d0' }}>Loading player data…</Typography>
    </Box>
  );

  if (error) return <Box sx={{ p: 3 }}><Typography sx={{ color: '#ef4444' }}>{error}</Typography></Box>;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 860, mx: 'auto' }}>

      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>⚖️ Start / Sit</Typography>
        <Typography variant="body2" sx={{ color: '#9eb8d0' }}>
          Compare two players and get a data-driven recommendation based on Fantasy Rank &amp; matchup.
        </Typography>
      </Box>

      {/* Position selector */}
      <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
        {POSITIONS.map(pos => (
          <Chip key={pos} label={pos} onClick={() => handlePositionChange(pos)} sx={{
            fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer',
            backgroundColor: position === pos ? 'rgba(46,168,75,0.18)' : 'rgba(16,32,52,0.62)',
            color: position === pos ? '#2ea84b' : '#9eb8d0',
            border: `1px solid ${position === pos ? '#2ea84b' : '#1e3448'}`,
            '&:hover': { backgroundColor: position === pos ? 'rgba(46,168,75,0.25)' : 'rgba(22,44,70,0.8)' },
          }} />
        ))}
      </Box>

      {/* Player search */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexDirection: { xs: 'column', sm: 'row' } }}>
        {[{ label: 'Player 1', value: player1, setter: setPlayer1 }, { label: 'Player 2', value: player2, setter: setPlayer2 }].map(({ label, value, setter }) => (
          <Autocomplete key={label} options={posPlayers} value={value} onChange={(_, v) => setter(v)}
            getOptionLabel={p => p ? `${p.name} (${p.team})` : ''} isOptionEqualToValue={(o, v) => o.id === v.id}
            PaperComponent={props => (
              <Paper {...props} sx={{ backgroundColor: '#0a1828', border: '1px solid #1e3448',
                '& .MuiAutocomplete-option': { color: '#e8f0f8', fontSize: '0.85rem',
                  '&[aria-selected="true"]': { backgroundColor: 'rgba(46,168,75,0.15)' },
                  '&.Mui-focused': { backgroundColor: 'rgba(22,44,70,0.8)' } } }} />
            )}
            renderInput={params => (
              <TextField {...params} label={label} placeholder={`Search ${position}…`} variant="outlined" size="small" sx={{
                '& .MuiOutlinedInput-root': { color: '#e8f0f8', backgroundColor: 'rgba(16,32,52,0.62)',
                  '& fieldset': { borderColor: '#1e3448' }, '&:hover fieldset': { borderColor: '#2ea84b' }, '&.Mui-focused fieldset': { borderColor: '#2ea84b' } },
                '& .MuiInputLabel-root': { color: '#9eb8d0' }, '& .MuiInputLabel-root.Mui-focused': { color: '#2ea84b' }, '& .MuiSvgIcon-root': { color: '#9eb8d0' },
              }} />
            )}
            sx={{ flex: 1 }} noOptionsText={<Typography variant="body2" sx={{ color: '#9eb8d0', px: 1 }}>No {position}s found</Typography>} />
        ))}
      </Box>

      {(!player1 || !player2) && (
        <Box sx={{ textAlign: 'center', py: 6, backgroundColor: 'rgba(16,32,52,0.42)', border: '1px dashed #1e3448', borderRadius: 2 }}>
          <Typography sx={{ fontSize: '2rem', mb: 1 }}>👆</Typography>
          <Typography variant="body1" sx={{ color: '#9eb8d0', fontWeight: 600 }}>Select two {position}s to compare</Typography>
          <Typography variant="caption" sx={{ color: '#556b7a' }}>Use the search boxes above to pick your players</Typography>
        </Box>
      )}

      {comparison && player1 && player2 && (
        <Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <PlayerCard player={player1} catMap={catMap} pos={position} isStart={comparison.p1Starts} nrRank={comparison.rank1} totalRanked={totalRanked} pts={comparison.pts1} matchup={comparison.matchup1} />
            <PlayerCard player={player2} catMap={catMap} pos={position} isStart={!comparison.p1Starts} nrRank={comparison.rank2} totalRanked={totalRanked} pts={comparison.pts2} matchup={comparison.matchup2} />
          </Box>
          <Box sx={{ backgroundColor: 'rgba(16,32,52,0.62)', border: '1px solid #1e3448', borderRadius: 2, p: 2 }}>
            <Typography variant="caption" sx={{ color: '#f5a623', fontWeight: 700, letterSpacing: '0.06em', display: 'block', mb: 1 }}>
              💡 RECOMMENDATION
            </Typography>
            <Typography variant="body2" sx={{ color: '#e8f0f8', lineHeight: 1.7 }}>{comparison.reasoning}</Typography>
            <Typography variant="caption" sx={{ color: '#556b7a', display: 'block', mt: 1.5 }}>
              * Fantasy Rank uses season-long stats with configurable scoring weights. Matchup ratings are approximate season-level estimates.
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
}
